import { useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  Button,
  Checkbox,
  Modal,
  Select,
  Text,
  useModalStackContext,
} from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { useDisclosure } from '@mantine/hooks'
import type { EmploymentVerificationAction } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  VoEActionStatus,
  VoEOutreachTrigger,
  VoEOutreachType,
} from 'src/graphql/types/verificationOfEmployments'
import {
  ADD_VOE_ACTION,
  CANCEL_VOE_ACTION,
  GET_VOE_ACTIONS,
  GET_VOE_CONTACT_ATTEMPT_COUNT,
  RESCHEDULE_VOE_ACTION,
} from 'src/graphql/voe.graphql'
import { useCompanyOutreachConfig } from 'src/hooks/useCompanyOutreachConfig'
import { formatDateCustom } from 'src/lib/formatters'

import {
  findNearbyAutomatedAction,
  getActionLabel,
  isAutomatedAction,
  nextLiveAction,
  OUTREACH_DATE_TIME_FORMAT,
  OUTREACH_ELIGIBLE_VOE_STATUSES,
  typeLabel,
} from './outreachActionLabels'

const RESCHEDULE_STACK_ID = 'voe-reschedule-modal'
const ADD_STACK_ID = 'voe-add-outreach-modal'

interface OutreachConfigFields {
  outreachTrigger?: string | null
  requiredManualAttempts?: number | null
  skipCurrentEmployer?: boolean | null
}

interface OutreachActions {
  actions: EmploymentVerificationAction[]
  /** The one attempt recruiters see; null when nothing is pending. */
  upcomingAction: EmploymentVerificationAction | null
  /** Why automation is not running yet, when there is something to say. */
  banner: React.ReactNode
  /** The reschedule and add modals; render once near the section. */
  modals: React.ReactNode
  openAdd: () => void
  openReschedule: (action: EmploymentVerificationAction) => void
  cancel: (actionId: string) => void
  cancellingActionId: string | null
}

/**
 * Loads a VoE's outreach actions and owns the reschedule, cancel and add
 * flows, so the audit trail can place the next attempt among its rows and the
 * admin table can list every action without duplicating the mutations.
 */
export const useOutreachActions = ({
  voeId,
  voeStatus,
}: {
  voeId: string
  voeStatus: string | null | undefined
}): OutreachActions => {
  const [addModalOpened, addModal] = useDisclosure(false)
  const [rescheduleAction, setRescheduleAction] =
    useState<EmploymentVerificationAction | null>(null)
  const [newScheduledAt, setNewScheduledAt] = useState<Date | null>(null)
  // The time the recruiter picked, to tell whether the API moved it.
  const requestedRescheduleAtRef = useRef<Date | null>(null)
  const [rescheduleWarning, setRescheduleWarning] = useState<string | null>(
    null
  )
  const [cancellingActionId, setCancellingActionId] = useState<string | null>(
    null
  )
  const [addType, setAddType] = useState<VoEOutreachType | null>(
    VoEOutreachType.EMAIL
  )
  const [addScheduledAt, setAddScheduledAt] = useState<Date | null>(new Date())
  const [alreadyCompleted, setAlreadyCompleted] = useState(false)
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(RESCHEDULE_STACK_ID)
        stackCtx.removeModal(ADD_STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { config } = useCompanyOutreachConfig()
  const outreachConfig = config as OutreachConfigFields | null
  const outreachTrigger =
    outreachConfig?.outreachTrigger ?? VoEOutreachTrigger.IMMEDIATELY
  const requiredManualAttempts = outreachConfig?.requiredManualAttempts ?? 1

  const { data, refetch } = useQuery(GET_VOE_ACTIONS, {
    variables: { voeId },
    fetchPolicy: 'network-only',
  })

  // The step rows decide whose move the verification is, so the tables that
  // show it reload too.
  const apolloClient = useApolloClient()
  const refetchLists = () =>
    apolloClient.refetchQueries({
      include: ['VerificationOfEmployments', 'VoETabCounts'],
    })

  const { data: voeCountData, refetch: refetchVoeCount } = useQuery(
    GET_VOE_CONTACT_ATTEMPT_COUNT,
    { variables: { id: voeId }, fetchPolicy: 'network-only' }
  )

  const actions: EmploymentVerificationAction[] = data?.voeActions ?? []
  const upcomingAction = nextLiveAction(actions, voeStatus)

  // Progress banner derived state — count both VoEAuditTrail contact attempts and
  // completed manual actions (e.g. "Log as already completed") which don't create
  // audit trail entries, without double-counting.
  //
  // When a manual action executes via Inngest, sendAutomatedVoEEmail creates a
  // VoEAuditTrail entry AND marks the action COMPLETED with auditTrailId set.
  // contactAttemptCount includes that audit trail entry; completedManualActions
  // also includes that action — so we subtract the overlap (actions that have a
  // linked auditTrailId) to avoid counting the same attempt twice.
  const contactAttemptCount =
    voeCountData?.verificationOfEmployment?.contactAttemptCount ?? 0
  const completedManualActions = actions.filter(
    (a) => !isAutomatedAction(a) && a.status === VoEActionStatus.COMPLETED
  )
  const linkedAuditTrailCount = completedManualActions.filter(
    (a) => a.auditTrailId != null
  ).length
  const completedManualCount =
    contactAttemptCount - linkedAuditTrailCount + completedManualActions.length
  const hasAutomatedActions = actions.some(
    (a) => isAutomatedAction(a) && a.status !== VoEActionStatus.SKIPPED
  )
  const showManualGateBanner =
    outreachTrigger === VoEOutreachTrigger.AFTER_MANUAL &&
    !hasAutomatedActions &&
    completedManualCount < requiredManualAttempts
  // Mirrors the API's skipCurrentEmployer gate: no end date = current job.
  const voeRequest = voeCountData?.verificationOfEmployment?.requestContent
  // Banner should only appear before any outreach has started and only while
  // the VoE is still eligible for outreach (open statuses).
  const outreachHasStarted =
    (contactAttemptCount ?? 0) > 0 ||
    actions.some((a) =>
      [
        VoEActionStatus.SCHEDULED,
        VoEActionStatus.IN_PROGRESS,
        VoEActionStatus.COMPLETED,
        VoEActionStatus.FAILED,
      ].includes(a.status)
    )
  const isOutreachEligible =
    voeStatus != null &&
    OUTREACH_ELIGIBLE_VOE_STATUSES.includes(voeStatus as string)
  const isCurrentEmployer = !!voeRequest && voeRequest.endDate == null
  const skipCurrentEmployer = !!outreachConfig?.skipCurrentEmployer
  const showCurrentEmployerBanner =
    skipCurrentEmployer &&
    isCurrentEmployer &&
    isOutreachEligible &&
    !outreachHasStarted

  const [cancelAction] = useMutation(CANCEL_VOE_ACTION, {
    onCompleted: () => {
      toast('Outreach action cancelled', 'success')
      setCancellingActionId(null)
      refetch()
      refetchVoeCount()
      refetchLists()
    },
    onError: (err) => {
      setCancellingActionId(null)
      toast(err.message, 'error')
    },
  })

  const [rescheduleVoEAction, { loading: rescheduling }] = useMutation(
    RESCHEDULE_VOE_ACTION,
    {
      onCompleted: (result) => {
        // Automated calls are snapped into the company's dialing window by the
        // API, so the saved time can differ from the one picked — say so.
        const savedAt = result?.rescheduleVoEAction?.scheduledAt
        const requestedAt = requestedRescheduleAtRef.current
        const moved =
          savedAt &&
          requestedAt &&
          Math.abs(new Date(savedAt).getTime() - requestedAt.getTime()) >=
            60 * 1000
        if (moved) {
          toast(
            `That time is outside the calling hours set for employment verification automated calls, so the call was moved to ${formatDateCustom(
              savedAt,
              OUTREACH_DATE_TIME_FORMAT
            )}`,
            'warning'
          )
        } else {
          toast('Outreach action rescheduled', 'success')
        }
        setRescheduleAction(null)
        refetch()
        refetchLists()
      },
      onError: (err) => toast(err.message, 'error'),
    }
  )

  const resetAddForm = () => {
    setAddType(VoEOutreachType.EMAIL)
    setAddScheduledAt(new Date())
    setAlreadyCompleted(false)
  }

  const [addVoEAction, { loading: adding }] = useMutation(ADD_VOE_ACTION, {
    onCompleted: () => {
      toast(
        alreadyCompleted ? 'Outreach attempt logged' : 'Outreach action added',
        'success'
      )
      addModal.close()
      resetAddForm()
      refetch()
      refetchVoeCount()
      refetchLists()
    },
    onError: (err) => toast(err.message, 'error'),
  })

  const cancel = (actionId: string) => {
    setCancellingActionId(actionId)
    cancelAction({ variables: { actionId } })
  }

  const getRescheduleWarning = (
    action: EmploymentVerificationAction,
    date: Date
  ): string | null => {
    if (action.configStepOrder == null) return null
    const laterStep = actions.find(
      (a) =>
        a.configStepOrder != null &&
        a.configStepOrder > action.configStepOrder! &&
        a.status === VoEActionStatus.SCHEDULED &&
        new Date(a.scheduledAt) < date
    )
    if (laterStep) {
      return `${getActionLabel(
        laterStep
      )} is scheduled before this new time, but since execution follows attempt numbers, it will still run after this one.`
    }
    const earlierStep = actions.find(
      (a) =>
        a.configStepOrder != null &&
        a.configStepOrder < action.configStepOrder! &&
        a.status === VoEActionStatus.SCHEDULED &&
        new Date(a.scheduledAt) > date
    )
    if (earlierStep) {
      return `${getActionLabel(
        earlierStep
      )} is scheduled after this new time, but will still run before this one.`
    }
    return null
  }

  const openReschedule = (action: EmploymentVerificationAction) => {
    const initial = new Date(action.scheduledAt)
    setRescheduleAction(action)
    setNewScheduledAt(initial)
    setRescheduleWarning(getRescheduleWarning(action, initial))
  }

  const closeReschedule = () => {
    setRescheduleAction(null)
    setRescheduleWarning(null)
  }

  const handleReschedule = () => {
    if (!rescheduleAction || !newScheduledAt) return
    requestedRescheduleAtRef.current = newScheduledAt
    rescheduleVoEAction({
      variables: {
        actionId: rescheduleAction.voeActionId,
        newScheduledAt: newScheduledAt.toISOString(),
      },
    })
  }

  const handleAdd = () => {
    if (!addType || !addScheduledAt) return
    addVoEAction({
      variables: {
        voeId,
        input: {
          type: addType,
          scheduledAt: addScheduledAt.toISOString(),
          markAsCompleted: alreadyCompleted,
        },
      },
    })
  }

  // A logged past attempt can't collide with a send, so only a schedule warns.
  const nearbyAutomatedAction =
    !alreadyCompleted && addType && addScheduledAt
      ? findNearbyAutomatedAction(actions, addType, addScheduledAt)
      : null
  const addWarning = nearbyAutomatedAction
    ? `An automated ${typeLabel(
        nearbyAutomatedAction.type
      ).toLowerCase()} already goes out ${formatDateCustom(
        nearbyAutomatedAction.scheduledAt,
        OUTREACH_DATE_TIME_FORMAT
      )}.`
    : null

  const handleAddModalClose = () => {
    addModal.close()
    resetAddForm()
  }

  const banner = showCurrentEmployerBanner ? (
    <div className="mb-2 rounded-md border border-doubleNickel-warning-300 bg-doubleNickel-warning-50 px-3 py-2">
      <Text size="xs" fw={500} className="text-doubleNickel-warning-700">
        Current employer — no automated outreach
      </Text>
      <Text size="xs" className="text-doubleNickel-warning-600">
        Your company settings skip employers the applicant still works for. Any
        outreach you send here goes to their current employer.
      </Text>
    </div>
  ) : showManualGateBanner &&
    // Never show manual-gate on terminal VoEs
    isOutreachEligible &&
    // When skipCurrentEmployer applies, hide the manual gate once outreach has started
    !(skipCurrentEmployer && isCurrentEmployer && outreachHasStarted) ? (
    <div className="mb-2 rounded-md border border-doubleNickel-warning-300 bg-doubleNickel-warning-50 px-3 py-2">
      <Text size="xs" fw={500} className="text-doubleNickel-warning-700">
        {completedManualCount} of {requiredManualAttempts} manual attempt
        {requiredManualAttempts !== 1 ? 's' : ''} completed
      </Text>
      <Text size="xs" className="text-doubleNickel-warning-600">
        Log {requiredManualAttempts - completedManualCount} more manual outreach
        attempt
        {requiredManualAttempts - completedManualCount !== 1 ? 's' : ''} to
        start the automated sequence.
      </Text>
    </div>
  ) : null

  const modals = (
    <>
      <Modal
        opened={!!rescheduleAction}
        onClose={closeReschedule}
        title="Reschedule Outreach"
        size="sm"
        stackId={RESCHEDULE_STACK_ID}
      >
        <div className="flex flex-col gap-4">
          <DateTimePicker
            label="New date and time"
            valueFormat={OUTREACH_DATE_TIME_FORMAT}
            value={newScheduledAt}
            onChange={(date) => {
              setNewScheduledAt(date)
              setRescheduleWarning(
                date && rescheduleAction
                  ? getRescheduleWarning(rescheduleAction, date)
                  : null
              )
            }}
            minDate={new Date()}
          />
          {rescheduleWarning && (
            <Text size="xs" className="text-doubleNickel-warning-600">
              ⚠ {rescheduleWarning}
            </Text>
          )}
          {rescheduleAction?.callAttempt != null && (
            <Text size="xs" className="text-doubleNickel-gray-500">
              Automated calls only go out during the calling hours in your
              Employment Verification calling settings. A time outside them
              moves to the next opening.
            </Text>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeReschedule}>
              Cancel
            </Button>
            <Button
              loading={rescheduling}
              disabled={!newScheduledAt}
              onClick={handleReschedule}
            >
              Reschedule
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        opened={addModalOpened}
        onClose={handleAddModalClose}
        title="Add Outreach Attempt"
        size="sm"
        stackId={ADD_STACK_ID}
      >
        <div className="flex flex-col gap-4">
          <Select
            label="Type"
            value={addType}
            onChange={(value) => setAddType(value as VoEOutreachType | null)}
            data={[
              {
                value: VoEOutreachType.EMAIL,
                label: typeLabel(VoEOutreachType.EMAIL),
              },
              {
                value: VoEOutreachType.CALL,
                label: `${typeLabel(VoEOutreachType.CALL)} (coming soon)`,
                disabled: true,
              },
              {
                value: VoEOutreachType.FAX,
                label: `${typeLabel(VoEOutreachType.FAX)} (coming soon)`,
                disabled: true,
              },
            ]}
          />
          <Checkbox
            label="Log as already completed"
            checked={alreadyCompleted}
            onChange={(e) => {
              setAlreadyCompleted(e.currentTarget.checked)
              setAddScheduledAt(new Date())
            }}
          />
          <DateTimePicker
            label={
              alreadyCompleted ? 'Date completed' : 'Scheduled date and time'
            }
            valueFormat={OUTREACH_DATE_TIME_FORMAT}
            value={addScheduledAt}
            onChange={setAddScheduledAt}
            maxDate={alreadyCompleted ? new Date() : undefined}
            minDate={alreadyCompleted ? undefined : new Date()}
          />
          {addWarning && (
            <Text size="xs" className="text-doubleNickel-warning-600">
              ⚠ {addWarning}
            </Text>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleAddModalClose}>
              Cancel
            </Button>
            <Button
              loading={adding}
              disabled={!addType || !addScheduledAt}
              onClick={handleAdd}
            >
              {alreadyCompleted ? 'Log Attempt' : 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )

  return {
    actions,
    upcomingAction,
    banner,
    modals,
    openAdd: addModal.open,
    openReschedule,
    cancel,
    cancellingActionId,
  }
}
